/**
 * Single source for "how should a date look on screen" across the app.
 * Supabase/native `<input type="date">` always use ISO (yyyy-mm-dd) --
 * nothing here ever changes that storage format, only how a person types
 * or reads a date. Driven by the CURRENT COMPANY's own country (see
 * hooks/useCompanyCountry.ts), not the viewer's device/browser locale, so
 * everyone looking at the same company's data sees the same format.
 */

export type DateFormatPattern = 'dd/mm/yyyy' | 'mm/dd/yyyy';

// Countries that write the month before the day. Everyone else in the
// world conventionally writes day-before-month, so that's the default
// rather than something every other country needs to be listed for.
const MM_DD_YYYY_COUNTRIES = new Set(['United States', 'USA', 'US']);

export function getDateFormatForCountry(country: string | null | undefined): DateFormatPattern {
  if (country && MM_DD_YYYY_COUNTRIES.has(country.trim())) return 'mm/dd/yyyy';
  return 'dd/mm/yyyy';
}

/** ISO -> the regional display string (e.g. "2026-12-18" -> "18/12/2026"). */
export function isoToDisplay(iso: string, format: DateFormatPattern): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!match) return '';
  const [, y, m, d] = match;
  return format === 'mm/dd/yyyy' ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
}

/** The regional display string -> ISO. Returns '' if it isn't a complete 8-digit date yet. */
export function displayToIso(display: string, format: DateFormatPattern): string {
  const digits = display.replace(/\D/g, '');
  if (digits.length !== 8) return '';
  const first = digits.slice(0, 2);
  const second = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  const [mm, dd] = format === 'mm/dd/yyyy' ? [first, second] : [second, first];
  return `${year}-${mm}-${dd}`;
}

/** Live-formats a raw digit string as the user types it, e.g. "181220" -> "18/12/20". */
export function maskDigitsAsDisplay(digits: string): string {
  const clean = digits.replace(/\D/g, '').slice(0, 8);
  return [clean.slice(0, 2), clean.slice(2, 4), clean.slice(4, 8)].filter(Boolean).join('/');
}

/** Rejects a syntactically-8-digit but nonexistent calendar date (e.g. 31/02/2026). */
export function isValidCalendarDate(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(`${iso}T00:00:00Z`);
  return date.getUTCFullYear() === y && date.getUTCMonth() + 1 === m && date.getUTCDate() === d;
}
